import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { IMG } from "@/constants/bbAssets";
import { scrollToId } from "@/lib/scroll";

const NAV_LINKS = [
  { label: "Ways to Play", id: "ways" },
  { label: "Why Beach Break", id: "why" },
  { label: "Meet Tess", id: "tess" },
  { label: "FAQ", id: "faq" },
];

export const Nav = () => {
  const [open, setOpen] = useState(false);

  const go = (id) => {
    setOpen(false);
    setTimeout(() => scrollToId(id), open ? 250 : 0);
  };

  return (
    <>
      <header className="fixed top-0 inset-x-0 z-50 bg-cream/90 backdrop-blur-md border-b border-deep/10">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 md:h-[76px] flex items-center justify-between gap-4">
          <button
            data-testid="nav-logo-home"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="flex items-center shrink-0"
            aria-label="Beach Break Volleyball — back to top"
          >
            <img src={IMG.logo} alt="Beach Break Sand Volleyball" className="h-14 md:h-16 w-auto" />
          </button>

          <nav className="hidden md:flex items-center gap-8">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-link-${l.id}`}
                onClick={() => go(l.id)}
                className="font-mono text-[11px] uppercase tracking-[0.18em] text-deep hover:text-sun transition-colors duration-200"
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              data-testid="nav-cta-join"
              onClick={() => go("join")}
              className="hidden sm:inline-flex items-center gap-1.5 bg-deep text-cream font-mono text-[11px] uppercase tracking-[0.18em] px-5 py-2.5 rounded-full hover:bg-sun hover:text-deep transition-colors duration-200"
            >
              Join the Interest List
              <ArrowUpRight className="w-3.5 h-3.5" />
            </button>
            <button
              data-testid="nav-mobile-toggle"
              className="md:hidden p-2 text-deep"
              onClick={() => setOpen((v) => !v)}
              aria-label={open ? "Close menu" : "Open menu"}
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="fixed inset-0 z-40 bg-cream md:hidden pt-24 px-6"
          >
            <div className="flex flex-col gap-2">
              {NAV_LINKS.map((l, i) => (
                <motion.button
                  key={l.id}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.3 }}
                  onClick={() => go(l.id)}
                  className="text-left font-display text-4xl uppercase text-deep py-3 border-b border-deep/10"
                >
                  {l.label}
                </motion.button>
              ))}
              <button
                data-testid="nav-mobile-cta"
                onClick={() => go("join")}
                className="mt-6 w-full bg-deep text-cream font-mono text-xs uppercase tracking-[0.18em] px-6 py-4 rounded-full"
              >
                Join the Interest List
              </button>
              <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.2em] text-deep/60 text-center">
                Good Vibes × Volleyball — Tampa, FL
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Nav;
